import axios from 'axios';
import { JobStatus } from '~/generated/prisma';
import {
  ActualCosts,
  JobResultsStats,
  ValidationJobResponse,
} from '~/generated/scraper';

export interface CreateExtractionJobData {
  urls: string[];
  maxToken: number;
  maxUrls: number;
  dataTypes?: string[];
  autoValidate?: boolean;
  maxDepth?: number;
  scrapeExternal?: boolean;
  aiPrioritization?: boolean;
  proxyRetryMode?: 'manual' | 'automatic';
  userGoal?: string;
}

export interface ExtractionTask {
  id: string;
  userId: string;
  jobId: string;
  urls: string[];
  dataTypes: string[];
  status: JobStatus;
  progress: number;
  creditsCost: number;
  webhookUrl?: string | null;
  autoValidate: boolean;
  maxDepth: number;
  maxUrls: number;
  scrapeExternal: boolean;
  validEmailsFile?: string | null;
  invalidEmailsFile?: string | null;
  validPhonesFile?: string | null;
  invalidPhonesFile?: string | null;
  unvalidatedEmailsFile?: string | null;
  unvalidatedPhonesFile?: string | null;
  stats?: JobResultsStats | null;
  costBreakdown?: ActualCosts | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface CreateExtractionJobResponse {
  taskId: string;
  jobId: string;
  status: string;
  creditsCost: number;
  urls: string[];
  dataTypes: string[];
}

/**
 * Create a new extraction job
 */
export const createExtractionJobApi = async (
  data: CreateExtractionJobData,
): Promise<CreateExtractionJobResponse> => {
  const response = await axios.post<{
    success: boolean;
    message: string;
    data: CreateExtractionJobResponse;
  }>('/api/scraper/create-job', data);
  return response.data.data;
};

/**
 * Get extraction job by job ID
 */
export const getExtractionJobApi = async (
  jobId: string,
): Promise<ExtractionTask> => {
  const response = await axios.get<{
    success: boolean;
    data: ExtractionTask;
  }>(`/api/scraper/jobs/${jobId}`);
  return response.data.data;
};

/**
 * Get extraction task by task ID
 */
export const getExtractionTaskApi = async (
  taskId: string,
): Promise<ExtractionTask> => {
  const response = await axios.get<{
    success: boolean;
    data: {
      task: ExtractionTask;
      scraperStatus: unknown;
    };
  }>(`/api/scraper/tasks/${taskId}`);
  return response.data.data.task;
};

/**
 * Get user's extraction tasks
 */
export const getExtractionTasksApi = async (params?: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ tasks: ExtractionTask[]; total: number }> => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append('status', params.status);
  if (params?.search) searchParams.append('search', params.search);
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.offset) searchParams.append('offset', params.offset.toString());

  const response = await axios.get<{
    success: boolean;
    data: ExtractionTask[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }>(`/api/scraper/tasks?${searchParams.toString()}`);

  return {
    tasks: response.data.data,
    total: response.data.pagination.total,
  };
};

/**
 * Cancel an extraction job
 */
export const cancelExtractionJobApi = async (
  jobId: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await axios.delete<{
    success: boolean;
    message: string;
  }>(`/api/scraper/jobs/${jobId}`);
  return response.data;
};

/**
 * Validate extraction task contacts
 */
export const validateExtractionJobApi = async (
  taskId: string,
  options?: {
    retry_validation?: boolean;
    max_token?: number;
  },
): Promise<{
  success: boolean;
  message: string;
  data: ValidationJobResponse;
}> => {
  const response = await axios.post<{
    success: boolean;
    message: string;
    data: ValidationJobResponse;
  }>(`/api/scraper/tasks/${taskId}/validate`, {
    retry_validation: options?.retry_validation ?? false,
    max_token: options?.max_token,
  });
  return response.data;
};

/**
 * Download extraction task results from contacts database
 */
export const downloadExtractionTaskApi = async (
  taskId: string,
  format: 'csv' | 'excel',
): Promise<Blob> => {
  const response = await axios.get(
    `/api/scraper/tasks/${taskId}/download?format=${format}`,
    {
      responseType: 'blob',
    },
  );
  return response.data;
};

/**
 * Validation cost data interface
 */
export interface ValidationCostData {
  unvalidatedEmails: number;
  unvalidatedPhones: number;
  emailCostPerItem: number;
  phoneCostPerItem: number;
  totalEmailCost: number;
  totalPhoneCost: number;
  totalCostInTokens: number;
  userBalance: number;
  hasSufficientBalance: boolean;
  maxToken: number;
}

/**
 * Get validation cost for an extraction task
 */
export const getValidationCostApi = async (
  taskId: string,
): Promise<ValidationCostData> => {
  const response = await axios.get<{
    success: boolean;
    data: ValidationCostData;
  }>(`/api/scraper/tasks/${taskId}/validation-cost`);
  return response.data.data;
};

/**
 * Chart data types
 */
export interface WeeklyActivityData {
  day: string;
  emails: number;
  phones: number;
}

export interface MonthlyJobsData {
  month: string;
  jobs: number;
}

export interface ChartsData {
  weeklyData: WeeklyActivityData[];
  monthlyData: MonthlyJobsData[];
}

/**
 * Get dashboard charts data
 * Returns weekly activity and monthly jobs data
 */
export const getChartsDataApi = async (): Promise<ChartsData> => {
  const response = await axios.get<{
    success: boolean;
    data: ChartsData;
  }>('/api/dashboard/charts');
  return response.data.data;
};

/**
 * Stats data types
 */
export interface StatsData {
  totalJobs: { value: number; trend: number; trendLabel: string };
  creditsUsed: { value: number; trend: number; trendLabel: string };
  emailsExtracted: { value: number; trend: number; trendLabel: string };
  phonesExtracted: { value: number; trend: number; trendLabel: string };
}

/**
 * Get dashboard stats data
 * Returns stats with month-over-month comparisons
 */
export const getStatsDataApi = async (): Promise<StatsData> => {
  const response = await axios.get<{
    success: boolean;
    data: StatsData;
  }>('/api/dashboard/stats');
  return response.data.data;
};

/**
 * Tasks stats data types
 */
export interface TasksStatsData {
  totalTasks: number;
  completedTasks: number;
  creditsUsed: number;
  activeTasks: number;
}

/**
 * Get tasks stats data
 * Returns total tasks, completed tasks, credits used, and active tasks
 */
export const getTasksStatsDataApi = async (): Promise<TasksStatsData> => {
  const response = await axios.get<{
    success: boolean;
    data: TasksStatsData;
  }>('/api/dashboard/tasks-stats');
  return response.data.data;
};

/**
 * Analytics stats data types
 */
export interface AnalyticsStatsData {
  totalExtractions: number;
  totalExtractionsTrend: number;
  emailsExtracted: number;
  emailsExtractedTrend: number;
  phonesExtracted: number;
  phonesExtractedTrend: number;
  avgSuccessRate: number;
  avgSuccessRateTrend: number;
  creditsUsed: number;
  validationsRun: number;
  totalTasks: number;
  activeTasks: number;
  periodLabel: string;
}

/**
 * Get analytics stats data
 * Returns comprehensive analytics stats based on date range
 * @param range - Date range: '7d', '30d', '90d', or 'all' (default: '30d')
 */
export const getAnalyticsStatsApi = async (
  range: '7d' | '30d' | '90d' | 'all' = '30d',
): Promise<AnalyticsStatsData> => {
  const response = await axios.get<{
    success: boolean;
    data: AnalyticsStatsData;
  }>(`/api/analytics?range=${range}`);
  return response.data.data;
};

/**
 * Analytics charts data types
 */
export interface AnalyticsChartsData {
  extractionActivity: {
    date: string;
    emails: number;
    phones: number;
    socials: number;
    validations: number;
  }[];
  creditDistribution: {
    name: string;
    value: number;
    color: string;
  }[];
  successRateTrend: {
    month: string;
    rate: number;
  }[];
}

/**
 * Get analytics charts data
 * Returns chart data for analytics page based on date range
 * @param range - Date range: '7d', '30d', '90d', or 'all' (default: '30d')
 */
export const getAnalyticsChartsDataApi = async (
  range: '7d' | '30d' | '90d' | 'all' = '30d',
): Promise<AnalyticsChartsData> => {
  const response = await axios.get<{
    success: boolean;
    data: AnalyticsChartsData;
  }>(`/api/analytics/charts?range=${range}`);
  return response.data.data;
};

/**
 * Task credit usage data types
 */
export interface TaskCreditUsageData {
  date: string;
  tasks: number;
  credits: number;
}

/**
 * Get task credit usage data
 * Returns daily task completion and credit consumption for last 7 days
 */
export const getTaskCreditUsageDataApi = async (): Promise<
  TaskCreditUsageData[]
> => {
  const response = await axios.get<{
    success: boolean;
    data: TaskCreditUsageData[];
  }>('/api/dashboard/task-credit-usage');
  return response.data.data;
};

/**
 * Top performing task data type
 */
export interface TopPerformingTask {
  id: string;
  jobId: string;
  urls: string[];
  dataTypes: string[];
  emails: number;
  phones: number;
  successRate: number;
  credits: number;
  date: string;
}

/**
 * Get top performing tasks data
 * Returns top performing tasks based on date range
 * @param range - Date range: '7d', '30d', '90d', or 'all' (default: '30d')
 */
export const getTopPerformingTasksApi = async (
  range: '7d' | '30d' | '90d' | 'all' = '30d',
): Promise<TopPerformingTask[]> => {
  const response = await axios.get<{
    success: boolean;
    data: TopPerformingTask[];
  }>(`/api/analytics/top-tasks?range=${range}`);
  return response.data.data;
};
